import { useState, type FormEvent } from "react";
import type { Child } from "../types";
import type { ChildInput } from "../lib/firestore";

type Props = {
  child: Child | null;
  onSubmit: (input: ChildInput) => Promise<void>;
  onCancel: () => void;
};

export function ChildForm({ child, onSubmit, onCancel }: Props) {
  const [name_en, setNameEn] = useState(child?.name_en ?? "");
  const [name_zh, setNameZh] = useState(child?.name_zh ?? "");
  const [date_of_birth, setDob] = useState(child?.date_of_birth ?? "");
  const [enrolled_on, setEnrolled] = useState(child?.enrolled_on ?? "");
  const [parent_contact, setParentContact] = useState(child?.parent_contact ?? "");
  const [gp_alias, setGpAlias] = useState(child?.gp_alias ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e: FormEvent) => {
    e.preventDefault();
    if (!name_en.trim() || !name_zh.trim() || !enrolled_on) {
      setError("English name, Chinese name and Enrolled-on are required");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        name_en: name_en.trim(),
        name_zh: name_zh.trim(),
        date_of_birth: date_of_birth ? date_of_birth : null,
        enrolled_on,
        ended_on: child?.ended_on ?? null,
        parent_contact: parent_contact.trim(),
        gp_alias: gp_alias.trim() ? gp_alias.trim() : null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-3 max-w-md">
      <label className="block">
        <span className="text-sm">English name</span>
        <input
          aria-label="English name"
          className="block w-full border rounded px-2 py-1"
          value={name_en}
          onChange={(e) => setNameEn(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="text-sm">Chinese name 中文姓名</span>
        <input
          aria-label="Chinese name"
          className="block w-full border rounded px-2 py-1"
          value={name_zh}
          onChange={(e) => setNameZh(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="text-sm">Date of birth (optional)</span>
        <input
          aria-label="Date of birth"
          type="date"
          className="block w-full border rounded px-2 py-1"
          value={date_of_birth ?? ""}
          onChange={(e) => setDob(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="text-sm">Enrolled on</span>
        <input
          aria-label="Enrolled on"
          type="date"
          className="block w-full border rounded px-2 py-1"
          value={enrolled_on}
          onChange={(e) => setEnrolled(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="text-sm">Google Photos search term (optional)</span>
        <input
          aria-label="Google Photos search term"
          placeholder="nickname Vera uses in Photos face search"
          className="block w-full border rounded px-2 py-1"
          value={gp_alias ?? ""}
          onChange={(e) => setGpAlias(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="text-sm">Parent contact (optional)</span>
        <input
          aria-label="Parent contact"
          className="block w-full border rounded px-2 py-1"
          value={parent_contact}
          onChange={(e) => setParentContact(e.target.value)}
        />
      </label>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={busy}
          className="bg-[#ff6b35] text-white rounded-full px-4 py-2 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="text-gray-600 px-4 py-2">
          Cancel
        </button>
      </div>
    </form>
  );
}
